import { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getMyWallets, getMyRewards, getWithdrawalSummary } from '../../api/walletApi';
import { getMyReferrals, getGenerationStatus, getMyRankHistory } from '../../api/userApi';
import { getMyFastrackNodes } from '../../api/fastrackApi';

const accent = '#cc66ff';
const cyan = '#00e5ff';
const cardDark = {
  background: 'radial-gradient(ellipse at 100% 0%, rgba(153,51,255,0.55) 0%, rgba(20,0,40,0.9) 45%, #0d0d0d 70%)',
  color: '#fff',
  border: '1px solid #9933ff44',
};
const fmt = (v) => v != null ? Number(v).toFixed(2) : '0.00';

const ITEMS = [
  { label: 'Fastrack', icon: '⚡', path: '/fastrack-status', color: accent },
  { label: 'Generation', icon: '⊕', path: '/generation-bonus', color: '#66aaff' },
  { label: 'Performance', icon: '🏆', path: '/performance-bonus', color: '#ffaa44' },
  { label: 'Reward', icon: '🎁', path: '/rewards-status', color: '#66ff99' },
  { label: 'Withdrawal Limits', icon: '📊', path: '/withdrawal-limits', color: '#ffaa44' },
];

const AccountStatus = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [referralCount, setReferralCount] = useState(null);
  const [totalBalance, setTotalBalance] = useState(null);
  const [totalEarning, setTotalEarning] = useState(null);
  const [totalWithdrawals, setTotalWithdrawals] = useState(null);

  useEffect(() => {
    getMyReferrals().then((res) => setReferralCount(res.data.count)).catch(() => setReferralCount(0));

    getMyWallets().then((res) => {
      const w = res.data.wallets;
      const total = (w.main_wallet?.balance ?? 0) + (w.earning_wallet?.balance ?? 0)
        + (w.one_wallet?.balance ?? 0) + (w.reward_wallet?.balance ?? 0);
      setTotalBalance(total);
    }).catch(() => setTotalBalance(0));

    getWithdrawalSummary().then((res) => {
      const total = (res.data.oneWallet?.successful ?? 0) + (res.data.external?.successful ?? 0);
      setTotalWithdrawals(total);
    }).catch(() => setTotalWithdrawals(0));

    Promise.all([
      getMyFastrackNodes().catch(() => ({ data: {} })),
      getGenerationStatus().catch(() => ({ data: {} })),
      getMyRankHistory().catch(() => ({ data: {} })),
      getMyRewards().catch(() => ({ data: {} })),
    ]).then(([fastrackRes, generationRes, rankRes, rewardsRes]) => {
      const fastrack = fastrackRes.data.summary?.totalBonusEarned ?? 0;
      const generation = generationRes.data.totalEarned ?? 0;
      const performance = (rankRes.data.payoutLogs || []).reduce((sum, p) => sum + (p.amountCredited || 0), 0);
      const reward = rewardsRes.data.balance ?? 0;
      setTotalEarning(fastrack + generation + performance + reward);
    });
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4">
        <div className="mb-4">
          <h4 style={{ color: '#fff', fontWeight: 700 }}>Account Status</h4>
          <div style={{ color: '#aaa', fontSize: 13 }}>View your bonus and reward earnings across all programs</div>
        </div>

        <Row className="g-3 mb-3">
          {/* Profile summary */}
          <Col xs={12} md={6}>
            <Card style={{ ...cardDark, border: '1px solid #9933ff88' }} className="p-3 h-100">
              <Card.Body>
                <div style={{ fontSize: 14, fontWeight: 700, color: accent, marginBottom: 12 }}>Profile</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px' }}>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Name</div>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{user?.name}</div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>User Code</div>
                    <div style={{ color: cyan, fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>{user?.userCode || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Latest Slot</div>
                    <div style={{ color: '#66ff99', fontSize: 13, fontWeight: 600 }}>{user?.planId?.planName || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Direct Referrals</div>
                    <div style={{ color: '#ffaa44', fontSize: 13, fontWeight: 600 }}>{referralCount !== null ? referralCount : '—'}</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Wallet summary */}
          <Col xs={12} md={6}>
            <Card style={{ ...cardDark, border: '1px solid #9933ff88' }} className="p-3 h-100">
              <Card.Body>
                <div style={{ fontSize: 14, fontWeight: 700, color: accent, marginBottom: 12 }}>Wallet Summary</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px 8px' }}>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Total Earning</div>
                    <div style={{ color: '#66ff99', fontSize: 14, fontWeight: 700 }}>{fmt(totalEarning)}</div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Total Withdrawals</div>
                    <div style={{ color: '#ff6666', fontSize: 14, fontWeight: 700 }}>{fmt(totalWithdrawals)}</div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>Total Balance</div>
                    <div style={{ color: cyan, fontSize: 14, fontWeight: 700 }}>{fmt(totalBalance)}</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3">
          {ITEMS.map((item) => (
            <Col xs={6} md={3} key={item.path}>
              <Card
                style={{ ...cardDark, cursor: 'pointer' }}
                className="p-3 h-100 dashboard-card text-center"
                onClick={() => navigate(item.path)}
              >
                <Card.Body className="d-flex flex-column align-items-center justify-content-center">
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{item.icon}</div>
                  <div style={{ color: item.color, fontWeight: 700, fontSize: 15 }}>{item.label}</div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </div>
  );
};

export default AccountStatus;
